import qs from "qs";
import { useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";

/**
 * A hook to manage query parameters in a React application using React Router DOM.
 * Returns an object with utility functions to interact with query parameters.
 * @returns An object containing functions to manage query parameters:
 *   - params: An object representing the current query parameters.
 *   - flatParams: An object representing the current query parameters with array values converted to comma-separated strings.
 *   - getParamsAsArray: A function to retrieve query parameters as an array.
 *   - setParam: A function to set a single query parameter.
 *   - clearParam: A function to clear a single query parameter.
 *   - setParams: A function to set multiple query parameters.
 *   - clearParams: A function to clear all query parameters.
 */
export function useQueryParams() {
  const navigate = useNavigate();
  const { pathname, search } = useLocation();

  const setParam = useCallback(
    (param: string, value?: string | string[], resetPage: boolean = false) => {
      const params = qs.parse(search, { ignoreQueryPrefix: true });

      if (value) {
        params[param] = value;
      } else if (params[param]) {
        delete params[param];
      }

      if (resetPage) {
        params["page"] = "1";
      }

      navigate({
        pathname,
        search: qs.stringify(params, { arrayFormat: "repeat" }),
      });
    },
    [navigate, pathname, search]
  );

  const setParams = useCallback(
    (
      data: Record<string, string | string[] | undefined>,
      resetPage: boolean = true
    ) => {
      const params = qs.parse(search, { ignoreQueryPrefix: true });

      if (resetPage) {
        params["page"] = "1";
      }

      Object.entries(data).forEach(([key, value]) => {
        if (value) {
          params[key] = value;
        } else if (params[key]) {
          delete params[key];
        }
      });

      navigate({
        pathname,
        search: qs.stringify(params, { arrayFormat: "repeat" }),
      });
    },
    [navigate, pathname, search]
  );

  const params =
    (qs.parse(search, { ignoreQueryPrefix: true }) as Record<
      string,
      string | string[]
    >) || {};

  const flatParams = Object.entries(params).reduce<Record<string, string>>(
    (prev, [param, value]) => ({
      ...prev,
      [param]: Array.isArray(value) ? value.join(",") : (value as string),
    }),
    {}
  );

  const getParamsAsArray = (param: string) => {
    const values: string[] = [];
    if (params[param]) {
      if (typeof params[param] === "string") {
        values.push(params[param].toString());
      } else {
        values.push(...params[param]);
      }
    }
    return values;
  };

  const getTypedParamsAsArray = <T extends string>(param: string): T[] => {
    const value = params[param];

    if (!value) return [];

    return (Array.isArray(value) ? value : [value]) as T[];
  };

  return {
    params,
    flatParams,
    getParamsAsArray,
    setParam,
    clearParam(param: string) {
      setParam(param);
    },
    setParams,
    clearParams: () => {
      navigate({ pathname });
    },
    getTypedParamsAsArray,
  };
}
